import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Activity, Snowflake, Wind, Target, Moon, Play, ExternalLink, Clock } from "lucide-react";

const PHASE_ICONS = { Snowflake, Wind, Target, Moon };

function ytEmbedUrl(url) {
  if (!url) return null;
  const m = url.match(/youtu\.?be(?:\.com)?\/(?:shorts\/)?([\w-]{6,})/);
  if (!m) return null;
  const id = m[1];
  const tMatch = url.match(/[?&]t=(\d+)/);
  return `https://www.youtube.com/embed/${id}${tMatch ? `?start=${tMatch[1]}` : ""}`;
}

function HybridChip({ h, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="rounded-xl px-5 py-4 text-left transition-all"
      style={{
        backgroundColor: active ? `${h.color}1f` : "var(--ink-2)",
        boxShadow: active ? `0 0 0 2px ${h.color}` : "inset 0 0 0 1px rgba(255,255,255,0.10)",
      }}
      data-testid={`recovery-chip-${h.letter}`}
    >
      <div className="flex items-center gap-3">
        <div className="font-display font-black text-4xl leading-none" style={{ color: h.color }}>{h.letter}</div>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Recovery — Hybrid {h.letter}</div>
          <div className="text-sm text-white font-semibold leading-tight">{h.headline}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-white/50 mt-2">
        <Clock className="h-3 w-3" /> ~{h.total_minutes} min
      </div>
    </button>
  );
}

export default function RecoveryRoutine({ defaultLetter = null }) {
  const [data, setData] = useState(null);
  const [activeLetter, setActiveLetter] = useState(defaultLetter || "A");

  useEffect(() => {
    api.get("/recovery-routine").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (defaultLetter) setActiveLetter(defaultLetter); }, [defaultLetter]);

  if (!data) return <div className="text-white/60 p-8">Loading…</div>;
  const active = data.hybrids.find((h) => h.letter === activeLetter) || data.hybrids[0];

  return (
    <div className="space-y-8" data-testid="recovery-routine">
      <div>
        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Module · Recovery Protocol</div>
        <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-1 text-white">Post-Throw Recovery</h1>
        <p className="text-sm text-white/50 mt-2">Prescribed daily arm-care routine — band work, plyo-care progressions, and mobility — calibrated to today's suggested hybrid. The heavier the throw day, the longer the recovery.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.hybrids.map((h) => (
          <HybridChip key={h.letter} h={h} active={h.letter === activeLetter} onClick={() => setActiveLetter(h.letter)} />
        ))}
      </div>

      {active && (
        <div className="rounded-xl ink-card p-6" data-testid={`recovery-detail-${active.letter}`}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="font-display font-black text-6xl leading-none" style={{ color: active.color }}>{active.letter}</div>
              <div>
                <h2 className="font-display font-bold text-2xl text-white">{active.headline}</h2>
                <p className="text-sm text-white/60 mt-1 max-w-xl">{active.summary}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Total time</div>
              <div className="font-display font-black text-3xl text-white font-mono-num">{active.total_minutes}<span className="text-white/40 text-base ml-1">min</span></div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {active?.blocks.map((blk, bi) => {
          const Icon = PHASE_ICONS[blk.icon] || Activity;
          return (
            <section key={bi} className="rounded-xl ink-card overflow-hidden" data-testid={`recovery-phase-${bi}`}>
              <header className="px-5 py-4 border-b border-[var(--ink-border)] flex items-center gap-3">
                <Icon className="h-5 w-5" style={{ color: active.color }} />
                <h3 className="font-display font-bold text-lg text-white">{blk.phase}</h3>
                <span className="ml-auto text-[10px] tracking-[0.2em] font-bold text-white/40 uppercase">{blk.window}</span>
              </header>
              <div className="divide-y divide-[var(--ink-border)]">
                {blk.items.map((it, i) => {
                  const embed = ytEmbedUrl(it.video);
                  return (
                    <div key={i} className="px-5 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-semibold text-white">{it.label}</div>
                        {it.video && (
                          <a href={it.video} target="_blank" rel="noreferrer" className="text-xs hover:underline flex items-center gap-1 shrink-0" style={{ color: active.color }}>
                            <Play className="h-3 w-3" /> Video <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                      <ul className="mt-2 space-y-1">
                        {it.drills.map((d, j) => (
                          <li key={j} className="text-sm text-white/70 pl-4 relative">
                            <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: active.color, opacity: 0.6 }} />
                            {d}
                          </li>
                        ))}
                      </ul>
                      {embed && (
                        <div className="mt-3 rounded-lg overflow-hidden ring-1 ring-white/10 aspect-video bg-black">
                          <iframe
                            src={embed}
                            title={it.label}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <div className="text-xs text-white/30 text-center pt-4 italic">Source: {data.source}</div>

      {data.why_no_ice && (
        <details className="rounded-xl ink-card overflow-hidden" data-testid="why-no-ice-block">
          <summary className="px-5 py-4 cursor-pointer flex items-center gap-3 text-white/85 font-semibold list-none">
            <span className="text-xl">🚫🧊</span>
            <span>Why isn't icing in this routine? <span className="text-white/40 font-normal ml-1 text-sm">(tap to expand the science)</span></span>
          </summary>
          <div className="px-5 pb-5 text-sm text-white/70 leading-relaxed border-t border-[var(--ink-border)] pt-4 whitespace-pre-line">
            {data.why_no_ice}
          </div>
        </details>
      )}
    </div>
  );
}
